import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, ClipboardList, User } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, Reveal } from "@/components/ui-bits";
import { ContactForm } from "@/components/Forms";
import { useSettings } from "@/hooks/useContent";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, ADDRESS, waLink, track, DHIRAJ_PHOTO } from "@/lib/site";

export default function Contact() {
  const { data: settings } = useSettings();
  const photo = settings?.key_person_photo || DHIRAJ_PHOTO;

  return (
    <>
      <Seo
        title="Contact Orvella Overseas — Indian Food Products Exporter"
        description="Contact Orvella Overseas Private Limited for quotations, samples and B2B sourcing requirements. Phone/WhatsApp +91 99735 19755 · info@orvellaoverseas.com · Patna, Bihar, India."
        path="/contact"
      />

      <PageHero
        eyebrow="Contact Us"
        title="Let's Build a Global Supply Relationship"
        sub="Share your product requirement — our export team responds with availability, specifications and a commercial quotation."
      />

      <section className="section-pad">
        <div className="container-x">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="contact-cta-cards">
            {[
              { label: "Call Us", value: PHONE_DISPLAY, href: PHONE_TEL, icon: Phone, testid: "contact-call-card", ev: "phone_click" },
              { label: "WhatsApp Us", value: PHONE_DISPLAY, href: waLink(), icon: MessageCircle, testid: "contact-whatsapp-card", ev: "whatsapp_click" },
              { label: "Email Us", value: EMAIL, href: `mailto:${EMAIL}`, icon: Mail, testid: "contact-email-card", ev: "email_click" },
              { label: "Request a Quote", value: "Submit your requirement", href: "/quote", icon: ClipboardList, testid: "contact-quote-card", ev: "quote_open" },
            ].map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <div className="card card-hover flex h-full items-center gap-4 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy text-gold-soft"><Icon size={20} strokeWidth={1.8} /></span>
                  <div className="min-w-0">
                    <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-mist">{c.label}</h2>
                    <p className="mt-1 truncate text-sm font-bold text-navy">{c.value}</p>
                  </div>
                </div>
              );
              return (
                <Reveal key={c.label} delay={i * 0.05}>
                  {c.href.startsWith("/") ? (
                    <Link to={c.href} data-testid={c.testid} onClick={() => track(c.ev)}>{inner}</Link>
                  ) : (
                    <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={c.testid} onClick={() => track(c.ev)}>{inner}</a>
                  )}
                </Reveal>
              );
            })}
          </div>

          <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            <div className="space-y-6">
              <Reveal>
                <div className="card p-6" data-testid="company-info-card">
                  <h2 className="font-serif text-xl font-semibold text-navy">{settings?.company_name || "Orvella Overseas Private Limited"}</h2>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dark">{settings?.tagline || "Connecting Global Opportunities"}</p>
                  <ul className="mt-5 space-y-3 text-sm text-ink">
                    <li className="flex items-start gap-3"><Phone size={16} className="mt-0.5 shrink-0 text-gold-dark" /> {settings?.phone || PHONE_DISPLAY}</li>
                    <li className="flex items-start gap-3"><Mail size={16} className="mt-0.5 shrink-0 text-gold-dark" /> {settings?.email || EMAIL}</li>
                    <li className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-gold-dark" /> {settings?.address || ADDRESS}</li>
                    <li className="flex items-start gap-3"><User size={16} className="mt-0.5 shrink-0 text-gold-dark" />
                      <span>{settings?.key_person_name || "Dhiraj Kumar"} — Export Contact · {settings?.key_person_phone || "+91 91993 19199"}</span>
                    </li>
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="card overflow-hidden">
                  <img src={photo} alt={`${settings?.key_person_name || "Dhiraj Kumar"} — export contact, Orvella Overseas`} loading="lazy" className="aspect-[4/3] w-full border-b-2 border-gold object-cover object-top" />
                  <p className="px-5 py-4 text-center text-sm">
                    <strong className="text-navy">{settings?.key_person_name || "Dhiraj Kumar"}</strong>
                    <span className="block text-xs text-mist">Your direct export contact — mobile & WhatsApp {settings?.key_person_phone || "+91 91993 19199"}</span>
                  </p>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-2xl font-semibold text-navy">Send an Enquiry</h2>
              <p className="mb-5 mt-1 text-sm text-mist">Fields marked with <span className="text-gold-dark">*</span> are required.</p>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
